import { Input, LoadingOverlay, Stack, Text } from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { openConfirmModal } from '@mantine/modals';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { DatasetsInfo, delDatasets, getDatasetsListDatasets } from '@mygpt/app';
import { getResourcesPrinciple } from 'packages/app/apis/services/admin';

import { SvgSearch } from '@/components/Common/icon';
import { DataFrozenTip } from '@/components/DataFrozenTip';
import { CloneModal } from '@/components/Datasets/CloneModal';
import { CloningTip } from '@/components/Datasets/CloningTip';
import DatasetsEdit from '@/components/Datasets/Create/datasets';
import { useDatasetStore } from '@/store/dataset';
import ShareSettingModal from '@/components/Chat/ShareSettingModal';
import { useGPTStore } from '@/store';

import ListItem from './item';

const DatasetsList: React.FC<{ refreshList?: number }> = ({ refreshList }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { assetScope } = useGPTStore();
  const isLocalMode = import.meta.env.VITE_MODE === 'local';
  const isCloudMode = !isLocalMode;

  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [list, setList] = useState<DatasetsInfo[]>([]);

  // 搜索功能
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 500);

  const { setCloneTarget, setCloningTipOpened } = useDatasetStore();

  // 控制编辑框
  const [opened, { close, open }] = useDisclosure(false);

  //当前操作对象
  const [actionInfo, setActionInfo] = useState<{
    type: 'edit' | 'delete' | 'clone';
    info: DatasetsInfo;
  }>();

  // 分享弹窗相关
  const [shareModalOpened, setShareModalOpened] = useState(false);
  const [currentDatasetId, setCurrentDatasetId] = useState<string | null>(null);

  const isCloning = useMemo(() => {
    return !!list.some((item) => ['cloning_target', 'cloning_source'].includes(item.data_status));
  }, [list]);

  const initList = async (showLoading = true, searchQuery?: string) => {
    if (showLoading) {
      setLoading(true);
    }
    const query = searchQuery !== undefined ? searchQuery : debouncedSearch;
    const res = await getDatasetsListDatasets({
      page: 1,
      size: 1000,
      query: query ? query.normalize('NFD') : undefined,
      // Cloud版传入公司ID参数：有组织ID则传入，否则不传（表示个人资产）
      ...(isCloudMode && assetScope && { company_id: assetScope }),
    });
    setList(res.data.items);
    setLoading(false);
  };

  const deleteConfirm = () => {
    if (deleteLoading) return;

    openConfirmModal({
      title: t('dataset.delete_modal.title'),
      centered: true,
      children: (
        <div className="py-50">
          <Text>{t('dataset.delete_modal.desc')}</Text>
          <Text c={'red'} mt={15}>
            {t('embeddings.chatbot.delete.warning')}
          </Text>
        </div>
      ),
      labels: { confirm: t('delete_btn'), cancel: t('alert_cancel') },
      onCancel: () => console.log('Cancel'),
      confirmProps: {
        color: 'red',
        loading: deleteLoading,
        onClick: () => {
          deleteDatasets();
        },
      },
    });
  };

  const deleteDatasets = () => {
    setDeleteLoading(true);
    delDatasets({ id: actionInfo?.info.id || '' })
      .then(() => {
        initList();
      })
      .finally(() => {
        setDeleteLoading(false);
      });
  };

  // 删除编辑操作
  useEffect(() => {
    if (!actionInfo?.info) return;
    if (['cloning_target', 'cloning_source'].includes(actionInfo.info.data_status)) {
      setCloningTipOpened(true);
      return;
    }
    if (actionInfo.type === 'edit') open();
    if (actionInfo.type === 'delete') deleteConfirm();
    if (actionInfo.type === 'clone') setCloneTarget(actionInfo?.info);
  }, [actionInfo]);

  useEffect(() => {
    initList();
  }, [refreshList, assetScope]);

  // 搜索关键词变化时重新加载列表
  useEffect(() => {
    initList(true, debouncedSearch);
  }, [debouncedSearch]);

  useEffect(() => {
    if (!isCloning) return;

    const interval = setInterval(() => {
      initList(false);
    }, 30000);

    return () => {
      clearInterval(interval);
    };
  }, [isCloning]);

  const frozenTipOpened = useMemo(() => {
    return list.some((dataset) => dataset.data_status === 'frozen');
  }, [list]);

  return (
    <>
      <LoadingOverlay mih={200} visible={loading} />
      {/* 搜索框 - 始终在顶部 */}
      <Input
        size="sm"
        className="w-full max-w-md mb-4"
        leftSection={<SvgSearch />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        placeholder={t('dataset.search_placeholder')}
        rightSectionPointerEvents="all"
        rightSection={
          search ? (
            <button type="button" onClick={() => setSearch('')} className="text-gray-400 hover:text-gray-600">
              ×
            </button>
          ) : null
        }
      />
      <DataFrozenTip visible={frozenTipOpened} />
      {!loading && (
        <>
          {!list.length && (
            <section className="flex items-center justify-center min-h-[200px] bg-white">{t('no_data')}</section>
          )}
          <Stack gap={'lg'}>
            {list.map((item, index) => {
              return (
                <section
                  className="cursor-pointer"
                  key={item.id}
                  onClick={() => {
                    navigate(`/datasets/${item.id}/data-source`);
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <ListItem
                        key={item.id}
                        info={item}
                        handleEdit={() => {
                          setActionInfo({
                            type: 'edit',
                            info: item,
                          });
                        }}
                        handleDelete={() => {
                          setActionInfo({
                            type: 'delete',
                            info: item,
                          });
                        }}
                        handleClone={() => {
                          setActionInfo({
                            type: 'clone',
                            info: item,
                          });
                        }}
                        onShare={
                          isLocalMode
                            ? async () => {
                                // 立即打开弹窗，数据加载由弹窗内部处理
                                setCurrentDatasetId(item.id);
                                setShareModalOpened(true);
                              }
                            : undefined
                        }
                      />
                    </div>
                  </div>
                </section>
              );
            })}
          </Stack>
        </>
      )}
      <DatasetsEdit info={actionInfo?.info} opened={opened} onClose={close} afterSubmit={initList} />
      <CloneModal
        callback={() => {
          initList();
        }}
      />
      <CloningTip />
      {/* 本地化部署分享设置弹窗 */}
      {isLocalMode && currentDatasetId && (
        <ShareSettingModal
          opened={shareModalOpened}
          onClose={() => {
            setShareModalOpened(false);
            setCurrentDatasetId(null);
          }}
          botId={currentDatasetId}
          dataType="DATASET"
          authorityCode="DATASET_VIEW"
        />
      )}
    </>
  );
};

export default DatasetsList;
